import { useEffect } from 'react';

import getFocusableElements from '@/ducks/a11y/helpers/getFocusableElements';

type useCustomArrowKeysProps = {
  allowCycling?: boolean;
  element: HTMLElement | null;
  elementsQuery?: string;
  focusableElementsQuery?: string;
  isHorizontalArrowsIncluded?: boolean;
};

function useCustomArrowKeys({
  allowCycling = false,
  element,
  elementsQuery,
  focusableElementsQuery,
  isHorizontalArrowsIncluded = true,
}: useCustomArrowKeysProps) {
  useEffect(() => {
    if (!element) return;

    const handleKeyDown = (event: KeyboardEvent) => {
      const { key } = event;
      const allElements = getFocusableElements(element, focusableElementsQuery);
      const elementsCount = allElements.length;

      if (elementsCount === 0) return;

      const currentIndex = allElements.findIndex((el) => el === document.activeElement);
      if (currentIndex === -1) return;

      let newIndex = currentIndex;

      const isForwardKey = key === 'ArrowDown' || (isHorizontalArrowsIncluded && key === 'ArrowRight');
      const isBackwardKey = key === 'ArrowUp' || (isHorizontalArrowsIncluded && key === 'ArrowLeft');

      if (isForwardKey) {
        event.preventDefault();
        if (currentIndex + 1 < elementsCount) {
          newIndex = currentIndex + 1;
        } else if (allowCycling) {
          newIndex = 0; // Loop to start
        } else {
          newIndex = currentIndex; // Stay at last
        }
      } else if (isBackwardKey) {
        event.preventDefault();
        if (currentIndex - 1 >= 0) {
          newIndex = currentIndex - 1;
        } else if (allowCycling) {
          newIndex = elementsCount - 1; // Loop to end
        } else {
          newIndex = currentIndex; // Stay at first
        }
      } else {
        return;
      }

      allElements[newIndex]?.focus();
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [element, isHorizontalArrowsIncluded, focusableElementsQuery, elementsQuery, allowCycling]);
}

export default useCustomArrowKeys;
